<template>
  <ListPage variant="page" v-model:pageSize="pageSize" v-slot="{ pageSizeOptions, setPageSize }">
    <div>
      <AddNewOrderModal v-if="showAddModal" :open="showAddModal" @close="showAddModal = false"
        :queryBillOfLading="queryBillOfLading" @addNewOrder="addNewOrderHandler" :queryCustomerId="dropOffCustomerId">
      </AddNewOrderModal>

      <div
        class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-200/50 tw-p-3 sm:tw-p-4">
        <div class="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center">
          <div class="tw-flex-1">
            <SearchInput v-model="searchInput" @search="performSearch"
              placeholder="Search order, container, BL, customer…" class="tw-w-full" />
          </div>
          <div class="tw-flex tw-justify-end">
            <button type="button" @click="$router.push('/orders/new')"
              class="tw-inline-flex tw-items-center tw-rounded-lg tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800">
              Add New Order
            </button>
          </div>
        </div>

        <div
          class="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-t tw-border-slate-200/70 tw-pt-3">
          <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
              <input type="checkbox" id="chk_show_imports" :checked="showImports" @click="toggleShowImports"
                class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300" />
              Imports
            </label>
            <label class="tw-inline-flex tw-items-center tw-gap-2 tw-text-sm tw-text-slate-700">
              <input type="checkbox" id="chk_show_exports" :checked="showExports" @click="toggleShowExports"
                class="tw-h-4 tw-w-4 tw-rounded tw-border-slate-300" />
              Exports
            </label>
          </div>

          <div class="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-justify-end tw-gap-3">
            <div class="tw-flex-shrink-0 tw-text-sm tw-text-slate-600 tw-whitespace-nowrap">
              <span v-if="ordersLoading">Loading…</span>
              <template v-else>
                <span v-if="pageCount > 1">Page {{ pageNum + 1 }} of {{ pageCount }}</span>
                <span v-if="pageCount > 1" class="tw-mx-2 tw-text-slate-400">·</span>
                <span>Showing {{ (filteredOrders || []).length }} orders</span>
                <span class="tw-mx-2 tw-text-slate-400">·</span>
                <label class="tw-inline-flex tw-items-center tw-gap-2">
                  <span>Rows</span>
                  <select
                    class="tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-px-2 tw-py-1 tw-text-sm tw-text-slate-800"
                    :value="String(pageSize)"
                    @change="(e) => setPageSize(Number((e.target as HTMLSelectElement).value))">
                    <option v-for="opt in pageSizeOptions" :key="opt" :value="String(opt)">{{ opt }}</option>
                  </select>
                </label>
              </template>
            </div>

            <div class="tw-min-w-0 tw-overflow-x-auto">
              <div class="tw-flex tw-flex-nowrap tw-gap-2 tw-whitespace-nowrap tw-justify-end">
                <button type="button"
                  class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                  :class="status === orderStatusOptions.AllActive
                    ? 'tw-border-transparent tw-bg-slate-900 tw-text-white'
                    : 'tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-text-slate-700 hover:tw-bg-white'"
                  @click="updateFilterStatus(orderStatusOptions.AllActive)">
                  <span>All Active</span>
                  <span
                    class="tw-inline-flex tw-min-w-[28px] tw-justify-center tw-rounded-full tw-bg-white/20 tw-px-2 tw-py-0.5 tw-text-xs">{{
                      nOrders.allActive }}</span>
                </button>
                <button type="button"
                  class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                  :class="status === orderStatusOptions.InProgress
                    ? 'tw-border-transparent tw-bg-slate-900 tw-text-white'
                    : 'tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-text-slate-700 hover:tw-bg-white'"
                  @click="updateFilterStatus(orderStatusOptions.InProgress)">
                  <span>In Progress</span>
                  <span
                    class="tw-inline-flex tw-min-w-[28px] tw-justify-center tw-rounded-full tw-bg-white/20 tw-px-2 tw-py-0.5 tw-text-xs">{{
                      nOrders.inProgress }}</span>
                </button>

                <button type="button"
                  class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                  :class="status === orderStatusOptions.Available
                    ? 'tw-border-transparent tw-bg-slate-900 tw-text-white'
                    : 'tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-text-slate-700 hover:tw-bg-white'"
                  @click="updateFilterStatus(orderStatusOptions.Available)">
                  <span>Available</span>
                  <span
                    class="tw-inline-flex tw-min-w-[28px] tw-justify-center tw-rounded-full tw-bg-emerald-500/20 tw-px-2 tw-py-0.5 tw-text-xs">{{
                      nOrders.available }}</span>
                </button>

                <button type="button"
                  class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                  :class="status === orderStatusOptions.NotAvailable
                    ? 'tw-border-transparent tw-bg-slate-900 tw-text-white'
                    : 'tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-text-slate-700 hover:tw-bg-white'"
                  @click="updateFilterStatus(orderStatusOptions.NotAvailable)">
                  <span>Not Available</span>
                  <span
                    class="tw-inline-flex tw-min-w-[28px] tw-justify-center tw-rounded-full tw-bg-rose-500/20 tw-px-2 tw-py-0.5 tw-text-xs">{{
                      nOrders.notAvailable }}</span>
                </button>

                <button type="button"
                  class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                  :class="status === orderStatusOptions.Complete
                    ? 'tw-border-transparent tw-bg-slate-900 tw-text-white'
                    : 'tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-text-slate-700 hover:tw-bg-white'"
                  @click="updateFilterStatus(orderStatusOptions.Complete)">
                  <span>Complete</span>
                  <span
                    class="tw-inline-flex tw-min-w-[28px] tw-justify-center tw-rounded-full tw-bg-white/20 tw-px-2 tw-py-0.5 tw-text-xs">{{
                      nOrders.complete }}</span>
                </button>
                <button type="button"
                  class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition"
                  :class="status === orderStatusOptions.Cancelled
                    ? 'tw-border-transparent tw-bg-slate-900 tw-text-white'
                    : 'tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-text-slate-700 hover:tw-bg-white'"
                  @click="updateFilterStatus(orderStatusOptions.Cancelled)">
                  <span>Cancelled</span>
                  <span
                    class="tw-inline-flex tw-min-w-[28px] tw-justify-center tw-rounded-full tw-bg-red/20 tw-px-2 tw-py-0.5 tw-text-xs">{{
                      nOrders.cancelled }}</span>
                </button>
                <button v-if="isListFiltered" type="button"
                  class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-slate-200 tw-bg-white/60 tw-backdrop-blur tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 tw-transition hover:tw-bg-white"
                  @click="resetListFilters">
                  <span>Reset filters</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="newOrderId"
          class="tw-mt-3 tw-rounded-xl tw-border tw-border-indigo-200 tw-bg-indigo-50 tw-px-4 tw-py-3 tw-text-sm tw-text-indigo-900">
          Created new order: <span class="tw-font-semibold">{{ newOrderId }}</span>
        </div>
      </div>

      <div v-for="order in sortedOrders" :key="order.id"></div>

      <div class="tw-mt-4">
        <ListPage variant="card" :loading="!!ordersLoading" :cardPadding="false">
          <OrderTable @updateSortBy="updateSortBy" @selectOrder="selectOrder" :orders="filteredOrders" :sortBy="sortBy"
            :sortOrder="sortOrder" :lastOpenedOrderId="lastOpenedOrderId"></OrderTable>
        </ListPage>
      </div>

      <div class="tw-mt-6 tw-flex tw-justify-center" v-if="pageCount > 1">
        <PaginationBar :page="pageNum" :totalPages="pageCount" :disabled="!!ordersLoading" @previousPage="prevPage"
          @nextPage="nextPage" @goToPage="goToPage" />
      </div>
      <br />
    </div>
  </ListPage>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Order } from '../../types';
import Orders from '../../gql/orders/Orders.gql';
import OrderStats from '../../gql/orders/OrderStats.gql';
import AddNewOrderModal from './AddNewOrderModal.vue';
import OrderTable from './OrderTable.vue';
import { OrderStatus } from './orders';
import SearchInput from '../core/SearchInput.vue';
import PaginationBar from '../core/PageElements/PaginationBar.vue';
import ListPage from '../core/ListPage.vue';
import {
  clearSavedListFilters,
  getIsListFiltersDifferentFromDefaults,
  loadSavedListFilters,
  saveListFilters,
} from '../../utils/savedListFilters';
import {
  loadSavedLastOpenedListItemId,
  saveLastOpenedListItemId,
} from '../../utils/savedLastOpenedListItem';

const ORDERS_LIST_FILTERS_PAGE_KEY = 'orders';

const ORDERS_DEFAULT_LIST_FILTERS = {
  status: OrderStatus.InProgress,
  showImports: true,
  showExports: true,
  searchTerm: '',
  sortBy: 'orderId',
  sortOrder: 'DESC',
  pageSize: 25,
};

export default defineComponent({
  components: {
    OrderTable,
    AddNewOrderModal,
    PaginationBar,
    SearchInput,
    ListPage,
  },
  watch: {
    pageSize() {
      const vm = this as any;
      if (vm.pageNum !== 0) {
        vm.pageNum = 0;
      }
      vm.cachedTotal = null;
    },
    savedListFilters(filters) {
      if ((this as any).isListFiltered) {
        saveListFilters(ORDERS_LIST_FILTERS_PAGE_KEY, filters);
      } else {
        clearSavedListFilters(ORDERS_LIST_FILTERS_PAGE_KEY);
      }
    },
  },
  data() {
    const savedListFilters = loadSavedListFilters(
      ORDERS_LIST_FILTERS_PAGE_KEY,
      ORDERS_DEFAULT_LIST_FILTERS,
    );
    return {
      queryCustomer: null,
      newOrderCustomer: undefined,
      billOfLading: undefined,
      dropOffCustomerId: undefined,
      searchTerm: savedListFilters.searchTerm,
      searchInput: savedListFilters.searchTerm,
      queryBillOfLading: undefined,
      newOrderId: undefined,
      showImports: savedListFilters.showImports,
      showExports: savedListFilters.showExports,
      showCancelled: true,
      sortBy: savedListFilters.sortBy,
      sortOrder: savedListFilters.sortOrder,
      pageNum: 0,
      pageSize: savedListFilters.pageSize,
      status: savedListFilters.status,
      lastOpenedOrderId: loadSavedLastOpenedListItemId(ORDERS_LIST_FILTERS_PAGE_KEY),
      orderStatusOptions: OrderStatus,
      ordersLoading: 0,
      totalCount: 0,
      orders: null,
      statsData: null,
      showAddModal: false,
      cachedTotal: null,
    };
  },

  computed: {
    nOrders(): any {
      if (!this.orders || !(this.orders as any).orders || !(this.orders as any).orderStats) {
        return {
          available: 0,
          complete: 0,
          notAvailable: 0,
          inProgress: 0,
          cancelled: 0,
        };
      }

      const stats = (this.orders as any).orderStats;

      return {
        allActive: stats.available + stats.inProgress + stats.notAvailable,
        available: stats.available,
        complete: stats.complete,
        notAvailable: stats.notAvailable,
        inProgress: stats.inProgress,
        cancelled: stats.cancelled,
      };
    },
    mappedBLs(): any[] {
      return (this as any).activeBls;
    },
    isSearching(): boolean {
      return (this as any).searchTerm.length > 0;
    },
    savedListFilters(): typeof ORDERS_DEFAULT_LIST_FILTERS {
      const vm = (this as any);
      return {
        status: vm.status,
        showImports: vm.showImports,
        showExports: vm.showExports,
        searchTerm: vm.searchTerm,
        sortBy: vm.sortBy,
        sortOrder: vm.sortOrder,
        pageSize: vm.pageSize,
      };
    },
    isListFiltered(): boolean {
      return getIsListFiltersDifferentFromDefaults(
        (this as any).savedListFilters,
        ORDERS_DEFAULT_LIST_FILTERS,
      );
    },
    filteredOrders(): any[] {
      if (this.orders && (this.orders as any).orders) {
        return (this.orders as any).orders;
      }
      return [];
    },
    sortedOrders(): any[] {
      return (this.orders as any)?.orders || [];
    },
    ordersNotAvailable(): Order[] { return (this as any).filteredOrders; },
    ordersAvailable(): Order[] { return (this as any).filteredOrders; },
    ordersInProgress(): any[] { return (this as any).filteredOrders; },
    ordersComplete(): any[] { return (this as any).filteredOrders; },
    ordersCancelled(): any[] { return (this as any).filteredOrders; },
    pageCount(): number {
      if (this.orders && (this.orders as any).pages) {
        return (this.orders as any).pages;
      }
      return 1;
    },
  },
  methods: {
    /** Puts every saved filter back to its default (savedListFilters watcher clears storage) */
    resetListFilters() {
      const vm = (this as any);
      Object.assign(vm, ORDERS_DEFAULT_LIST_FILTERS);
      vm.searchInput = ORDERS_DEFAULT_LIST_FILTERS.searchTerm;
      vm.pageNum = 0;
      vm.cachedTotal = null;
    },
    updateFilterStatus(status: string) {
      (this as any).status = status;
      (this as any).pageNum = 0;
      (this as any).cachedTotal = null;
    },
    showContainer(showContainerUrl: string) {
      (this as any).$router.push(showContainerUrl);
    },
    toggleShowImports() {
      const vm = (this as any);
      vm.showImports = !vm.showImports;
      vm.pageNum = 0;
      vm.cachedTotal = null;
    },
    toggleShowExports() {
      const vm = (this as any);
      vm.showExports = !vm.showExports;
      vm.pageNum = 0;
      vm.cachedTotal = null;
    },
    selectOrder(orderIdx: any) {
      const vm = (this as any);
      const desRoute = `/orders/${orderIdx}`;
      vm.lastOpenedOrderId = String(orderIdx);
      saveLastOpenedListItemId(ORDERS_LIST_FILTERS_PAGE_KEY, orderIdx);
      if (vm.$router.currentRoute.path !== desRoute) {
        vm.$router.push(desRoute);
      }
    },
    addNewOrder(billOfLading: any, dropOff: any) {
      const vm = (this as any);

      vm.showAddModal = true;

      if (billOfLading) {
        vm.queryBillOfLading = billOfLading;
        vm.dropOffCustomerId = dropOff;
      }

      // Modal open/close is controlled reactively via showAddModal.
    },
    prevPage() {
      const vm = (this as any);
      if (vm.ordersLoading) return;
      if (vm.pageNum > 0) {
        vm.pageNum -= 1;
      }
    },
    nextPage() {
      const vm = (this as any);
      if (vm.ordersLoading) return;
      if (vm.pageNum + 1 >= vm.pageCount) return;
      vm.pageNum += 1;
    },
    goToPage(idx: number) {
      const vm = (this as any);
      if (vm.ordersLoading || idx === vm.pageNum) return;
      vm.pageNum = idx;
    },
    updateSortBy(sortBy: string) {
      const vm = (this as any);
      if (vm.sortBy === sortBy) {
        vm.sortOrder = vm.sortOrder === 'ASC' ? 'DESC' : 'ASC';
      } else {
        vm.sortBy = sortBy;
        vm.sortOrder = 'DESC';
      }
      vm.pageNum = 0;
    },
    selectBL(idx: any) {
      const bl = (this as any).activeBls[idx];
      (this as any).billOfLading = bl;
    },
    addNewOrderHandler(id: string) {
      const vm = (this as any);
      vm.newOrderId = id;
      if (
        vm.$router.currentRoute.path.includes('/orders') &&
        (vm.$router.currentRoute.path !== '/orders/' ||
          vm.$route.query.billOfLading)
      ) {
        vm.$router.replace('/orders/');
      }
      vm.$apollo.queries.orders.refetch();
    },
    updateOrders() {
      (this as any).$apollo.queries.orders.refetch();
    },
    performSearch(searchValue: any) {
      const vm = (this as any);
      vm.pageNum = 0;
      vm.cachedTotal = null;

      if (typeof searchValue === 'string') {
        vm.searchTerm = searchValue;
      } else {
        vm.searchTerm = vm.searchInput;
      }

      if (vm.searchInput !== vm.searchTerm) {
        vm.searchInput = vm.searchTerm;
      }
    },
  },
  apollo: {
    orders: {
      query: Orders,
      debounce: 200, // Decreased slightly for snappier filters
      loadingKey: 'ordersLoading',
      error(err) {
        console.error(err);
      },
      variables() {
        return {
          searchQuery: (this as any).searchTerm,
          status: (this as any).status,
          pageNum: (this as any).pageNum,
          pageSize: (this as any).pageSize,
          showImports: (this as any).showImports,
          showExports: (this as any).showExports,
          showCancelled: this.showCancelled,
          sortBy: (this as any).sortBy,
          sortOrder: (this as any).sortOrder,
          knownTotalCount: (this as any).pageNum > 0 ? (this as any).cachedTotal : null,
        };
      },
      result(result) {
        const vm = (this as any);
        // Cache the total count from the server response
        if (result && result.data && result.data.orders) {
          vm.cachedTotal = result.data.orders.total;
        }

        if ((this as any).$route.fullPath.includes('new')) {
          (this as any).$nextTick(() => {
            (this as any).addNewOrder(undefined);
          });
        } else if ((this as any).$route.query.billOfLading) {
          if ((this as any).$route.query.dropoff) {
            (this as any).$nextTick(() => {
              (this as any).addNewOrder(
                (this as any).$route.query.billOfLading,
                (this as any).$route.query.dropoff,
              );
            });
          } else {
            (this as any).$nextTick(() => {
              (this as any).addNewOrder(
                (this as any).$route.query.billOfLading,
              );
            });
          }
        }
      },
    },
  },
});
</script>

<style lang="scss">
/* Old styles preserved */
.uk-icon[uk-icon="icon: file-pdf"] svg {
  color: red;
}

.uk-background-sections {
  background-color: #b1d5e5;
}

.close-btn {
  position: absolute;
  top: 5px;
  right: 10px;
}

.mt-0 {
  margin-top: 0px;
}

.rel {
  position: relative;
}

.add-new-load-btn-slide-enter-active,
.slide-enter-active {
  -moz-transition-duration: 0.1s;
  -webkit-transition-duration: 0.1s;
  -o-transition-duration: 0.1s;
  transition-duration: 0.1s;
  -moz-transition-timing-function: ease-in;
  -webkit-transition-timing-function: ease-in;
  -o-transition-timing-function: ease-in;
  transition-timing-function: ease-in;
}

.slide-leave-active {
  -moz-transition-duration: 0.2s;
  -webkit-transition-duration: 0.2s;
  -o-transition-duration: 0.2s;
  transition-duration: 0.2s;
  -moz-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  -webkit-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  -o-transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
  transition-timing-function: cubic-bezier(0, 1, 0.5, 1);
}

.add-new-load-btn-slide-enter-to,
.add-new-load-btn-slide-leave,
.slide-enter-to,
.slide-leave {
  max-height: 100px;
  overflow: hidden;
}

.add-new-load-btn-slide-enter,
.slide-enter,
.slide-leave-to {
  overflow: hidden;
  max-height: 0;
}

/* NEW STYLES FOR SPINNER */
.orders-table-wrapper {
  position: relative;
  min-height: 300px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
  backdrop-filter: blur(1px);
}
</style>